import { useEffect, useState } from "react";

/**
 * Devuelve una versión "debounced" de un valor: la salida solo se actualiza
 * después de que el valor de entrada se haya estabilizado durante `delay` ms.
 *
 * @template T - tipo del valor a debounciar
 * @param {T} value - Valor de entrada cuyo cambio debe debounciarse
 * @param {number} [delay=300] - Retardo en milisegundos antes de propagar el valor
 * @returns {T} - Valor debounced; actualiza cuando `value` permanece sin cambios por `delay` ms
 * @throws {Error} - No lanza excepciones por diseño; en entornos sin temporizadores el comportamiento puede variar
 *
 * @example
 * // Uso básico en un componente React (TypeScript)
 * function SearchInput() {
 *   const [q, setQ] = useState("");
 *   const debouncedQ = useDebouncedValue(q, 500); // espera 500ms
 *
 *   useEffect(() => {
 *     if (debouncedQ) {
 *       // llamar a la API solo cuando la búsqueda se "estabiliza"
 *       fetch(`/api/search?q=${encodeURIComponent(debouncedQ)}`)
 *         .then(r => r.json())
 *         .then(data => console.log(data));
 *     }
 *   }, [debouncedQ]);
 *
 *   return <input value={q} onChange={e => setQ(e.target.value)} />;
 * }
 */
export function useDebouncedValue<T>(value: T, delay = 300) {
  const [debounced, setDebounced] = useState<T>(value);

  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);

  return debounced;
}
